import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, fmtDate, isOverdue } from "@/lib/constants";
import { Paperclip, ListChecks, Flag } from "lucide-react";
import QuickAssign from "@/components/task/QuickAssign";

export const KanbanBoard = ({ tasks, memberMap, members, onOpenTask, onStatusChange, canManage, onSaved, excludeStatuses = [] }) => {
  const navigate = useNavigate();
  const memberList = members || Object.values(memberMap || {});
  const [openAssignId, setOpenAssignId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const grouped = useMemo(() => {
    const g = {};
    STATUS_ORDER.forEach((s) => (g[s] = []));
    // Guard unknown/legacy statuses so a task never silently vanishes from the board.
    tasks.forEach((t) => {
      const s = STATUS_META[t.status] ? t.status : "assigned";
      (g[s] = g[s] || []).push(t);
    });
    return g;
  }, [tasks]);

  const handleDrop = (status) => {
    setOverCol(null);
    const id = dragId;
    setDragId(null);
    if (!id || !canManage) return;
    const t = tasks.find((x) => x.task_id === id);
    if (t && t.status !== status) onStatusChange(id, status);
  };

  const columns = useMemo(
    () => STATUS_ORDER.filter((s) => !excludeStatuses.includes(s)),
    [excludeStatuses]
  );

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
      {columns.map((s) => {
        const meta = STATUS_META[s];
        const list = grouped[s] || [];
        return (
          <div
            key={s}
            onDragOver={(e) => { if (canManage) { e.preventDefault(); setOverCol(s); } }}
            onDragLeave={() => setOverCol((c) => (c === s ? null : c))}
            onDrop={() => handleDrop(s)}
            className={`flex-shrink-0 w-72 rounded-sm border transition-colors ${overCol === s ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white"}`}
            data-testid={`kanban-column-${s}`}
          >
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-zinc-100 sticky top-0" style={{ background: meta.bg }}>
              <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
              <span className="text-xs font-semibold ml-auto" style={{ color: meta.color }}>{list.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {list.map((t) => {
                const cl = t.checklist || [];
                return (
                  <div
                    key={t.task_id}
                    draggable={canManage}
                    onDragStart={() => setDragId(t.task_id)}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    onClick={() => onOpenTask(t)}
                    className={`rounded-sm border border-zinc-200 bg-white p-3 cursor-pointer hover:border-zinc-400 hover:shadow-sm transition-all ${dragId === t.task_id ? "opacity-50" : ""}`}
                    data-testid={`kanban-card-${t.task_id}`}
                  >
                    <p className="text-sm font-medium leading-snug mb-1 break-words">{t.name}</p>
                    {t.project_name && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/project/${t.project_id}`); }}
                        className="text-[11px] text-zinc-500 hover:text-zinc-900 hover:underline mb-2 block truncate text-left max-w-full"
                        title={`Open ${t.project_name}`}
                        data-testid={`kanban-card-project-${t.task_id}`}
                      >
                        {t.builder ? `${t.builder} · ` : ""}{t.project_name}
                      </button>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span onClick={(e) => e.stopPropagation()}>
                        <QuickAssign task={t} members={memberList} memberMap={memberMap} onSaved={onSaved}
                          open={openAssignId === t.task_id}
                          onOpenChange={(o) => setOpenAssignId(o ? t.task_id : null)} />
                      </span>
                      {t.end_date && (
                        <span className={`text-[11px] ${isOverdue(t) ? "text-rose-600 font-semibold" : "text-zinc-500"}`}>{fmtDate(t.end_date)}</span>
                      )}
                      {t.priority && t.priority !== "none" && (
                        <Flag className="h-3 w-3" fill={PRIORITY_META[t.priority].color} style={{ color: PRIORITY_META[t.priority].color }} />
                      )}
                      {cl.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400 ml-auto"><ListChecks className="h-3 w-3" />{cl.filter((c) => c.done).length}/{cl.length}</span>
                      )}
                      {t.attachments?.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400"><Paperclip className="h-3 w-3" />{t.attachments.length}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {list.length === 0 && <p className="text-[11px] text-zinc-300 text-center py-4">No tasks</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
